import { ChangeEvent, ReactElement, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, openModal } from '@ff-it/ui';
import useAttachmentUploader from 'modules/attachments/useAttachmentUploader';
import { Attachment } from 'modules/attachments/types';
import { formatError } from 'utilities';
import { useCreateDefaults } from './useCreateDefaults';
import { ExtractResultDialog } from './ExtractResultDialog';
import { extractDialog } from './style.css';

/** Upload an invoice PDF -> review the extraction -> create the expense from it. */
export function UploadButton(): ReactElement {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { upload } = useAttachmentUploader<File, Error>({});
  const defaultInitialValues = useCreateDefaults();

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }
    setBusy(true);
    let attachment: Attachment;
    try {
      attachment = await upload(file);
    } catch (err) {
      toast.error(`Upload failed: ${formatError(err) || 'unknown error'}`);
      return;
    } finally {
      setBusy(false);
    }
    const extractedAndReviewedValues = await openModal<Record<string, unknown> | undefined>(
      ({ onSubmit, onDismiss }) => (
        <ExtractResultDialog
          attachmentId={attachment.id}
          fileUrl={attachment.file}
          onSubmit={(v) => onSubmit(v)}
          onDismiss={onDismiss}
        />
      ),
      { className: extractDialog, dismissProps: { outsidePress: false } },
    );
    if (extractedAndReviewedValues) {
      const initialValues = {
        ...defaultInitialValues,
        // only the fields that have a value; empty ones would overwrite the defaults with nothing
        ...Object.fromEntries(
          Object.entries(extractedAndReviewedValues).filter(([, v]) => v !== undefined && v !== null && v !== ''),
        ),
        attachments: [attachment],
      };
      navigate('create', {
        state: { initialValues },
      });
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" accept="application/pdf" hidden onChange={onFile} />
      <Button
        icon="file-pdf"
        size="sm"
        variant="secondary"
        mr="1"
        spin={busy}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Uploading…' : 'Upload'}
      </Button>
    </>
  );
}
